import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/auth";

export default function Resources() {
  const [items, setItems] = useState([]);
  const [searchParams] = useSearchParams();
  // Honor an incoming ?category= filter (used by the Wellness orb →
  // resources mapping). No new content — just applies the existing filter
  // the page already supports internally.
  const initialCategory = searchParams.get("category") || "all";
  const [filter, setFilter] = useState(initialCategory);

  useEffect(() => {
    api.get("/portal/resources").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const categories = ["all", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B]">Resource Center</h1>
        <p className="text-[#4B5563] mt-2">Verified, easy-to-understand guidance for every step.</p>
      </div>
      {/* Phase 10 F-TT-01 / F-TT-02 · 44px minimum vertical target on
          mobile + explicit vertical gap at narrow widths. */}
      <div className="flex gap-2 gap-y-2 flex-wrap" role="group" aria-label="Filter resources by category">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilter(c)}
            aria-pressed={filter === c}
            className={`px-4 py-2 min-h-[44px] rounded-full text-xs font-semibold transition border focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0B132B] focus-visible:outline-offset-2 ${
              filter === c ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-[#0B132B] border-gray-200 hover:border-[#0B132B]"
            }`}
          >
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((r) => {
          const hasUrl = typeof r.url === "string" && r.url.length > 0 && r.url !== "#";
          /* Phase 10 F-R-06 · do not render a dead "#" anchor when a
             resource has no URL.  Degrade to a non-interactive card. */
          const commonInner = (
            <>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#23803F] mb-2">{r.category}</div>
              <h3 className="font-bold text-[#0B132B] mb-2">{r.title}</h3>
              <p className="text-sm text-[#4B5563] mb-4">{r.summary}</p>
              {hasUrl ? (
                <span className="text-xs font-semibold text-[#1A5FAB] inline-flex items-center gap-1">
                  Read <ArrowRight className="w-3 h-3" aria-hidden="true" />
                </span>
              ) : (
                <span className="text-xs font-semibold text-[#4B5563]">Reference</span>
              )}
            </>
          );
          return hasUrl ? (
            <a
              key={r.id}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-300 hover:shadow-md transition block focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1A5FAB] focus-visible:outline-offset-2"
            >
              {commonInner}
            </a>
          ) : (
            <div
              key={r.id}
              className="bg-white rounded-2xl border border-gray-100 p-6 block"
            >
              {commonInner}
            </div>
          );
        })}
      </div>
    </div>
  );
}
